import { Form, SubmitFormButton } from '@calendly/form'
import { object, string, mixed } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { Segment } from '@calendly/segment'
import { Button } from '@calendly/ui/components/button'
import { Loader } from '@calendly/ui/components/loader'
import { Body, Footer } from '@calendly/ui/components/popup'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { currentOrganizationSelector } from 'selectors/current_organization'

import { ADD_TO_EXISTING_ACTION, CREATE_AND_ADD_ACTION } from './constants'
import { FormContent } from './form_content'
import { isUserGroupsInitializedSelector, hasUserGroupsSelector, isCompletedSelector } from './selectors'
import { addToGroup, fetchUserGroups, setCompleted } from './slice'

const LoadingState = ({ onClose }) => (
  <>
    <Loader data-testid="groups-loader" />

    <Footer>
      <Button decoration="secondary-outline" onClick={onClose}>
        {I18n.t('publisher.action.cancel')}
      </Button>
      <Button disabled>
        {I18n.t('publisher.organization.members.add_to_group.add')}
      </Button>
    </Footer>
  </>
)

export const AddToGroupForm = ({ onClose, onSuccess, organizationMembershipIds, organizationInvitationIds, subtitle }) => {
  const dispatch = useDispatch()
  const isLoading = !useSelector(isUserGroupsInitializedSelector)
  const hasUserGroups = useSelector(hasUserGroupsSelector)
  const isCompleted = useSelector(isCompletedSelector)

  const { data: { id: userId } } = useGetUserQuery()
  const { id: organizationId } = useSelector(currentOrganizationSelector)

  useEffect(() => {
    if (isCompleted) {
      onSuccess()
      onClose()
      dispatch(setCompleted(false))
    }
  }, [dispatch, isCompleted, onClose, onSuccess])

  useEffect(() => {
    if (!hasUserGroups) {
      dispatch(fetchUserGroups())
    }
  }, [dispatch, hasUserGroups])

  const validationSchema = object({
    action: string(),
    name: string().when('action', {
      is: CREATE_AND_ADD_ACTION,
      then: string().max(50).required()
    }),
    existingGroupId: mixed().when('action', {
      is: ADD_TO_EXISTING_ACTION,
      then: mixed().required(),
      otherwise: mixed()
    })
  })

  const handleSubmit = ({ action, name, existingGroupId }) => {
    Segment.track('user_profile_sidebar.add_to_group_clicked', { userId, organizationId })
    return dispatch(
      addToGroup({
        action,
        name,
        existingGroupId,
        organizationMembershipIds,
        organizationInvitationIds
      })
    )
  }

  if (isLoading) {
    return (<LoadingState onClose={onClose} />)
  }

  return (
    <Form
      initialValues={{
        name: '',
        action: hasUserGroups ? ADD_TO_EXISTING_ACTION : CREATE_AND_ADD_ACTION,
        existingGroupId: null
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <Body>
        <FormContent subtitle={subtitle} />
      </Body>

      <Footer>
        <Button
          decoration="secondary-outline"
          onClick={onClose}
        >
          {I18n.t('publisher.action.cancel')}
        </Button>
        <SubmitFormButton>
          {I18n.t('publisher.organization.members.add_to_group.add')}
        </SubmitFormButton>
      </Footer>
    </Form>
  )
}
